"use client";

/**
 * FaroProvider — wraps the app with Grafana Faro React integration.
 *
 * Provides:
 *  - FaroErrorBoundary: captures React render errors and sends them to Faro
 *  - Next.js page-view tracking via router events
 *  - useFaro() hook available to all child components
 */
import React, { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { FaroErrorBoundary } from "@grafana/faro-react";
import { faroEvents, initialiseFaro } from "@/runtime-utils/observability/faro";
import logger from "@/runtime-utils/observability/logger";

interface FaroProviderProps {
  children: React.ReactNode;
}

/**
 * Inner component — must be inside the Next.js router context.
 * Tracks page views on every route change.
 */
function FaroPageTracker() {
  const router = useRouter();
  const navStartRef = useRef<number>(0);

  useEffect(() => {
    initialiseFaro(); // idempotent — safe to call multiple times

    const onRouteChangeStart = () => {
      navStartRef.current = performance.now();
    };

    const onRouteChangeComplete = (url: string) => {
      const duration = performance.now() - navStartRef.current;
      faroEvents.pageView(url, Math.round(duration));
    };

    router.events.on("routeChangeStart", onRouteChangeStart);
    router.events.on("routeChangeComplete", onRouteChangeComplete);

    return () => {
      router.events.off("routeChangeStart", onRouteChangeStart);
      router.events.off("routeChangeComplete", onRouteChangeComplete);
    };
  }, [router.events]);

  return null;
}

/**
 * Fallback UI rendered when the error boundary catches a React error.
 * Kept intentionally minimal — the full error is sent to Faro/Grafana.
 */
function ErrorFallback({
  error,
  resetError,
}: {
  error: Error;
  resetError: () => void;
}) {
  // Faro receives the original error through its boundary; keep this prop in
  // the fallback signature so the SDK's callback contract remains explicit.
  void error;

  return (
    <div
      role="alert"
      style={{ padding: "2rem", textAlign: "center", fontFamily: "sans-serif" }}
    >
      <h2>Something went wrong</h2>
      <p style={{ color: "#666", marginBottom: "1rem" }}>
        The error has been reported. Please try again.
      </p>
      <button
        onClick={resetError}
        style={{
          padding: "0.5rem 1.5rem",
          background: "#1F4970",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
        }}
      >
        Try again
      </button>
    </div>
  );
}

export default function FaroProvider({ children }: FaroProviderProps) {
  return (
    <FaroErrorBoundary
      fallback={(error, resetError) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
      onError={(error) => {
        // Additional context logged alongside the Faro event
        logger.error("[FaroErrorBoundary]", error);
      }}
    >
      <FaroPageTracker />
      {children}
    </FaroErrorBoundary>
  );
}
